import { cn } from "@reactive-resume/utils";
import { forwardRef } from "react";

type PictureSource = {
  /** MIME type, e.g. `image/avif`, `image/webp`. */
  type: string;
  /** Path to the source asset. Pass a single URL or `srcSet` for responsive. */
  srcSet: string;
  /** Optional `sizes` attribute. */
  sizes?: string;
};

export type PictureProps = {
  /** Fallback `<img>` source — used when no `<source>` matches. Required. */
  src: string;
  /** Optional list of higher-priority sources (AVIF/WebP/etc). Tried in order. */
  sources?: PictureSource[];
  /** Alt text — required for accessibility. */
  alt: string;
  /** Native HTML width — strongly recommended to prevent CLS. */
  width?: number;
  /** Native HTML height — strongly recommended to prevent CLS. */
  height?: number;
  /** Native loading hint. Default `"lazy"`; pass `"eager"` for the LCP image. */
  loading?: "lazy" | "eager";
  /** Native fetchpriority hint. Default `"auto"`; pass `"high"` for the LCP image. */
  fetchPriority?: "auto" | "high" | "low";
  /** Native decoding hint. Default `"async"`. */
  decoding?: "async" | "sync" | "auto";
  /** Optional className applied to the inner `<img>`. */
  className?: string;
  /** Optional className applied to the `<picture>` wrapper. */
  pictureClassName?: string;
} & Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src" | "loading" | "decoding">;

/**
 * Production-grade `<picture>` primitive.
 *
 * - Emits `<source type="image/avif">`, `<source type="image/webp">`, and a
 *   fallback `<img>` so browsers pick the best format they support.
 * - Defaults to `loading="lazy"` + `decoding="async"` so non-LCP images
 *   stay out of the critical-paint path. Override for the hero image:
 *   `<Picture loading="eager" fetchPriority="high" />`.
 * - Always pass `width` and `height` to reserve layout space — prevents
 *   layout-shift contributions to CLS (perf audit item #2).
 *
 * @example
 *   <Picture
 *     src="/screenshots/builder.jpg"
 *     sources={[
 *       { type: "image/avif", srcSet: "/screenshots/builder.avif" },
 *       { type: "image/webp", srcSet: "/screenshots/builder.webp" },
 *     ]}
 *     alt="Apply Edge resume builder UI"
 *     width={1200}
 *     height={800}
 *   />
 */
export const Picture = forwardRef<HTMLImageElement, PictureProps>(
  (
    {
      src,
      sources = [],
      alt,
      width,
      height,
      loading = "lazy",
      fetchPriority = "auto",
      decoding = "async",
      className,
      pictureClassName,
      ...rest
    },
    ref,
  ) => (
    <picture className={pictureClassName}>
      {sources.map((source, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <source key={index} type={source.type} srcSet={source.srcSet} sizes={source.sizes} />
      ))}
      <img
        ref={ref}
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        decoding={decoding}
        // React 19+: `fetchPriority` is camelCase; older React lib types
        // map it to lowercase HTML — keep both spellings to be safe.
        fetchPriority={fetchPriority}
        className={cn(className)}
        {...rest}
      />
    </picture>
  ),
);

Picture.displayName = "Picture";
